import logger from "./logger";

interface PerformanceProcess {
  name: string;
  startTime: number;
  metadata?: Record<string, unknown>;
}

/**
 * Utility class for tracking and logging the performance of named processes.
 *
 * This class allows you to start and end timing for arbitrary processes,
 * optionally associating metadata with each process. When a process is ended,
 * it logs the duration and any associated metadata.
 */

export class Performance {
  private static processes: Map<string, PerformanceProcess> = new Map();

  /**
   * Starts tracking a process.
   * @param name Name of the process
   * @param metadata Optional metadata to associate with the process
   * @returns processId string
   */
  static startProcess(name: string, metadata?: Record<string, unknown>): string {
    const processId = `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    this.processes.set(processId, {
      name,
      startTime: Date.now(),
      metadata,
    });
    return processId;
  }

  /**
   * Ends tracking a process and logs the execution details.
   * @param processId The process ID returned by startProcess
   * @param extraLogData Optional extra data to log
   */
  static endProcess(processId: string, extraLogData?: Record<string, unknown>): void {
    const process = this.processes.get(processId);
    if (!process) {
      logger.warn(`Performance: Tried to end unknown processId: ${processId}`);
      return;
    }
    const endTime = Date.now();
    const duration = endTime - process.startTime;
    const meta = { ...process.metadata, ...extraLogData };
    const metaString = Object.keys(meta).length > 0 ? ` | meta: ${JSON.stringify(meta)}` : "";
    const durationSeconds = (duration / 1000).toFixed(2);
    logger.performance(`Process '${process.name}' [${processId}] finished in ${durationSeconds}s${metaString}`);
    this.processes.delete(processId);
  }
}
